import { getToken, BASE_URL } from './tokenService.js';
import { log } from '../utils/log.js';

/**
 * Default timeout for API requests in milliseconds
 */
const API_TIMEOUT = 30000;

/**
 * Creates an AbortController with a timeout
 * @param {number} timeout - Timeout in milliseconds
 * @returns {{ controller: AbortController, timeoutId: number }}
 */
function createTimeoutController(timeout = API_TIMEOUT) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

/**
 * Handles API response and parses JSON
 * @param {Response} response - Fetch response
 * @returns {Promise<object>} Parsed JSON response
 * @throws {Error} If response is not OK or JSON parsing fails
 */
async function handleResponse(response) {
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const responseText = await response.text();

  if (!responseText.trim()) {
    throw new Error('Empty response from server');
  }

  try {
    return JSON.parse(responseText);
  } catch (parseError) {
    throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}`);
  }
}

/**
 * Makes an authenticated API request with timeout support
 * @param {string} endpoint - API endpoint path (relative to BASE_URL)
 * @param {object} body - Request body (will be merged with token)
 * @param {object} options - Additional options
 * @param {number} options.timeout - Request timeout in milliseconds
 * @returns {Promise<object>} API response data
 */
export async function apiRequest(endpoint, body = {}, options = {}) {
  const { timeout = API_TIMEOUT } = options;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const token = await getToken();
    const url = `${BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token,
        ...body
      }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La requête a expiré. Veuillez réessayer.');
    }

    throw error;
  }
}

/**
 * Fetches product data by option ID
 * @param {string} optionId - Product option ID
 * @returns {Promise<object>} Product data
 */
export async function fetchProduct(optionId) {
  log.info(`Fetching product data for option ID: ${optionId}`);

  const result = await apiRequest('/API/Produits/produit-options-list-v2.php', {
    option_id: optionId
  });

  if (result.status === 'success' && result.count > 0) {
    log.info('Product API response:', result.data[0]);
    return result.data[0];
  }

  throw new Error('Produit non trouvé');
}

/**
 * Validates a client by ID
 * @param {string} clientId - Client ID
 * @returns {Promise<object>} Client data
 */
export async function validateClient(clientId) {
  log.info(`Validating client ID: ${clientId}`);

  const result = await apiRequest('/API/Clients/clients-list-v2.php', {
    client_id: clientId
  });

  if (result.status === 'success' && result.count > 0) {
    return result.data[0];
  }

  throw new Error('Client non trouvé');
}

/**
 * Loads dossier products
 * @param {number} dossierId - Dossier ID
 * @returns {Promise<object>} Dossier data with items
 */
export async function loadDossier(dossierId) {
  log.info(`Loading dossier ID: ${dossierId}`);

  const result = await apiRequest('/API/Sales/dossiers-list-v2.php', {
    fromId: parseInt(dossierId, 10),
    toId: parseInt(dossierId, 10)
  });

  if (result.status === 'success' && result.count > 0) {
    return result.data[0];
  }

  throw new Error('Dossier non trouvé');
}

/**
 * Creates a new dossier
 * @param {number} clientId - Client ID
 * @param {Array<{product_id: number, quantity: number}>} products - Products to add
 * @returns {Promise<object>} Created dossier data
 */
export async function createDossier(clientId, products) {
  log.info(`Creating dossier for client ID: ${clientId}`);

  const result = await apiRequest('/API/Sales/dossier-create-update.php', {
    action: 'create_dossier',
    client_id: parseInt(clientId, 10),
    products
  });

  if (result.status === 'success' && result.dossier_id) {
    return result;
  }

  throw new Error(result.message || 'Erreur lors de la création du dossier');
}

/**
 * Adds a product to an existing dossier
 * @param {number} dossierId - Dossier ID
 * @param {number} clientId - Client ID
 * @param {number} productId - Product ID
 * @param {number} quantity - Quantity
 * @returns {Promise<object>} Updated product data
 */
export async function addProductToDossier(dossierId, clientId, productId, quantity) {
  log.info(`Adding product ${productId} to dossier ${dossierId}`);

  const result = await apiRequest('/API/Sales/dossier-create-update.php', {
    action: 'add_product',
    client_id: parseInt(clientId, 10),
    dossier_id: parseInt(dossierId, 10),
    product_id: parseInt(productId, 10),
    quantity: parseInt(quantity, 10)
  });

  if (result.status === 'success' && result.product) {
    return result;
  }

  throw new Error(result.message || "Erreur lors de l'ajout du produit");
}

/**
 * Updates product quantity in dossier
 * @param {number} dossierProduitId - Dossier product ID
 * @param {number} quantity - New quantity
 * @returns {Promise<object>} Update result
 */
export async function updateDossierProductQuantity(dossierProduitId, quantity) {
  log.info(`Updating quantity for dossier product ${dossierProduitId} to ${quantity}`);

  const result = await apiRequest('/API/Sales/dossier-create-update.php', {
    action: 'update_product_quantity',
    dossier_produit_id: parseInt(dossierProduitId, 10),
    quantity: parseInt(quantity, 10)
  });

  if (result.status === 'success') {
    return result;
  }

  throw new Error(result.message || 'Erreur lors de la mise à jour de la quantité');
}

/**
 * Removes a product from dossier
 * @param {number} dossierProduitId - Dossier product ID
 * @returns {Promise<object>} Removal result
 */
export async function removeProductFromDossier(dossierProduitId) {
  log.info(`Removing dossier product ${dossierProduitId}`);

  const result = await apiRequest('/API/Sales/dossier-create-update.php', {
    action: 'remove_product',
    dossier_produit_id: parseInt(dossierProduitId, 10)
  });

  if (result.status === 'success') {
    return result;
  }

  throw new Error(result.message || 'Erreur lors de la suppression du produit');
}

/**
 * Updates dossier reference
 * @param {number} dossierId - Dossier ID
 * @param {string} reference - Reference text (max 255 chars, empty to clear)
 * @returns {Promise<object>} Update result
 */
export async function updateDossierReference(dossierId, reference) {
  log.info(`Updating reference for dossier ${dossierId}: ${reference}`);

  const result = await apiRequest('/API/Sales/dossier-create-update.php', {
    action: 'update_dossier_reference',
    dossier_id: parseInt(dossierId, 10),
    dossier_reference: reference
  });

  if (result.status === 'success') {
    return result;
  }

  throw new Error(result.message || 'Erreur lors de la mise à jour de la référence');
}
